import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { FileDownloadOutlined } from '@mui/icons-material';
import { useWindowSize } from '../../../../../utils/hooks';
import { months, responsiveSizeTimesheet, timesheetsStatuses } from '../../../../../utils/constants';
import classes from './TimesheetTableWrapper.module.scss';
import '../../../../../scss/weekpicker.css';
import InfoSign from '../../../../common/InfoSign/InfoSign';
import KeyValueLabel from '../../../../common/labels/KeyValueLabel/KeyValueLabel';
import StatusLabel from '../../../../common/labels/StatusLabel/StatusLabel';

import TimeandExpenseActions from '../../../../common/buttons/TimeandExpenseActions/TimeandExpenseActions';
import InfoIcon from '../../../../common/Icons/InfoIcon';
import { submissionEnum } from '../../../../../utils/enums';
import colors from '../../../../../scss/colors';
import MainButton from '../../../../common/buttons/MainButton';
import { buttonVariants } from '../../../../common/buttons/types';

export default function TimesheetTableWrapper({
  data,
  saving,
  rejecting,
  save,
  submit,
  approve,
  reject,
  withdraw,
  exportHandler,
  isRejectActive,
  submissionType,
  timesheetInfo,
  currentMonth,
  placementStatus,
  placementHandler,
  children,
  sectionHandler,
  isSplitApprovalFlow
}) {
  const { t } = useTranslation();
  const size = useWindowSize();

  const isSameMonth = dayjs(data?.[0]?.TimesheetHoursByDates?.[0]?.Day).isSame(
    data?.[0]?.TimesheetHoursByDates?.at(-1)?.Day,
    'month'
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        {submissionType === submissionEnum.WEEKLY && size.width > responsiveSizeTimesheet && (
          <div className={classes.period}>
            <p className={classes.text}>{`${
              dayjs(data[0]?.TimesheetHoursByDates[0].Day).isSame(data[0]?.TimesheetHoursByDates.at(-1).Day, 'month')
                ? dayjs(data[0]?.TimesheetHoursByDates[0].Day).format('D')
                : dayjs(data[0]?.TimesheetHoursByDates[0].Day).format('D MMM')
            } - ${dayjs(data[0]?.TimesheetHoursByDates.at(-1).Day).format('D MMM YYYY')}`}</p>
          </div>
        )}
        {submissionType === submissionEnum.MONTHLY && size.width > responsiveSizeTimesheet && (
          <div className={classes.period}>
            <p className={classes.textMonthly}>{months(t)[currentMonth]}</p>
          </div>
        )}
        {submissionType === submissionEnum.BI_MONTHLY && size.width > responsiveSizeTimesheet && (
          <div className={classes.period}>
            <p className={classes.textMonthly}>
              {currentMonth !== undefined && months(t)[currentMonth]
                ? `${months(t)[currentMonth]} (${dayjs(data[0]?.TimesheetHoursByDates[0].Day).format('D')} - ${dayjs(
                    data[0]?.TimesheetHoursByDates.at(-1).Day
                  ).format('D')})`
                : `${dayjs(data[0]?.TimesheetHoursByDates[0].Day).format('MMMM')} (${dayjs(
                    data[0]?.TimesheetHoursByDates[0].Day
                  ).format('D')} - ${dayjs(data[0]?.TimesheetHoursByDates.at(-1).Day).format('D')})`}
            </p>
          </div>
        )}
        {submissionType === submissionEnum.BI_WEEKLY && size.width > responsiveSizeTimesheet && (
          <div className={classes.period}>
            <p className={classes.textMonthly}>
              {`${dayjs(data?.[0]?.TimesheetHoursByDates?.[0].Day).format('MMMM D')} - ${
                isSameMonth
                  ? dayjs(data?.[0]?.TimesheetHoursByDates?.at(-1)?.Day).format('D')
                  : dayjs(data?.[0]?.TimesheetHoursByDates?.at(-1)?.Day).format('MMMM D')
              }`}
            </p>
          </div>
        )}
        <div />
        {![timesheetsStatuses(t).processed.apiStatus].includes(timesheetInfo.status) &&
          (timesheetInfo.isApprover || timesheetsStatuses(t).approved.apiStatus !== timesheetInfo.status) &&
          size.width > responsiveSizeTimesheet && (
            <KeyValueLabel
              label=""
              value={
                <StatusLabel
                  type={timesheetInfo?.status.toLowerCase()}
                  text={timesheetInfo?.status}
                  isApprover={timesheetInfo.isApprover}
                />
              }
            />
          )}
        {
          size.width > responsiveSizeTimesheet &&
            ![timesheetsStatuses(t).processed.apiStatus].includes(timesheetInfo.status) &&
            (!timesheetInfo.isApprover || timesheetsStatuses(t).rejected.apiStatus !== timesheetInfo.status) &&
            (timesheetInfo.isApprover || timesheetsStatuses(t).approved.apiStatus !== timesheetInfo.status)
          // <InfoSignRenderComponent timesheetInfo={timesheetInfo} isApprover={timesheetInfo.isApprover} size={size} />
        }
        {/* {size.width < responsiveSizeTimesheet && created && infoSignRenderComponent()} */}
        {size.width > responsiveSizeTimesheet &&
          ([timesheetsStatuses(t).processed.apiStatus].includes(timesheetInfo.status) ||
          (!timesheetInfo.isApprover && [timesheetsStatuses(t).approved.apiStatus].includes(timesheetInfo.status)) ||
          (timesheetInfo.isApprover && timesheetInfo.status === timesheetsStatuses(t).rejected.apiStatus) ||
          (timesheetInfo.isApprover &&
            isSplitApprovalFlow &&
            ![
              timesheetsStatuses(t).inreview.apiStatus,
              timesheetsStatuses(t).pending.apiStatus,
              timesheetsStatuses(t).approved.apiStatus // <--- ADD THIS HERE
            ].includes(timesheetInfo.sectionStatus)) ? (
            <div
              className={`${
                timesheetInfo.isApprover ? classes.infoApproverContainer : classes.infoContainer
              } !justify-center !items-center`}
            >
              {[timesheetsStatuses(t).processed.apiStatus].includes(timesheetInfo.status) && (
                <MainButton
                  size="small"
                  variant={buttonVariants.SECONDARY}
                  outlined
                  rounded
                  action={exportHandler}
                  forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
                  sx={{
                    marginRight: '10px'
                  }}
                >
                  <div className="flex items-center gap-1 justify-center">
                    <FileDownloadOutlined />
                    <span>{t('common.general.export')}</span>
                  </div>
                </MainButton>
              )}
              <InfoSign
                text={timesheetInfo.isApprover && `${timesheetInfo?.user?.FirstName} ${timesheetInfo?.user?.LastName}`}
                icon={<InfoIcon />}
              >
                <Box
                  sx={{
                    width: 'max-content',
                    padding: '32px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: '14px',
                    backgroundColor: colors.PAGE_BACKGROUND,
                    boxShadow: size.width > responsiveSizeTimesheet ? '0 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
                    borderRadius: '20px'
                  }}
                >
                  <KeyValueLabel
                    label={
                      timesheetInfo.isApprover
                        ? t('dashboard.timesheet.header.contractor')
                        : t('dashboard.timesheet.header.client')
                    }
                    value={
                      timesheetInfo.isApprover
                        ? `${timesheetInfo?.user?.FirstName} ${timesheetInfo?.user?.LastName}`
                        : timesheetInfo?.client
                    }
                  />
                  <KeyValueLabel label={t('dashboard.timesheet.header.jobtitle')} value={timesheetInfo?.placement} />
                  <KeyValueLabel label={t('dashboard.timesheet.header.order')} value={timesheetInfo?.po} />
                  <KeyValueLabel label={t('dashboard.timesheet.header.recordn')} value={timesheetInfo?.recordNo} />
                </Box>
              </InfoSign>

              <KeyValueLabel
                label=""
                value={
                  <StatusLabel
                    type={timesheetInfo.status.toLowerCase()}
                    text={timesheetInfo.status}
                    isApprover={timesheetInfo.isApprover}
                  />
                }
              />
            </div>
          ) : (
            <div className={classes.actions}>
              <TimeandExpenseActions
                save={save}
                submit={submit}
                approve={approve}
                reject={reject}
                withdraw={withdraw}
                isRejectActive={isRejectActive}
                submissionType={submissionType}
                saving={saving}
                rejecting={rejecting}
                isApprover={timesheetInfo.isApprover}
                status={timesheetInfo?.status}
                sectionStatus={timesheetInfo.sectionStatus}
                placementStatus={placementStatus}
                placementHandler={placementHandler}
                sectionHandler={sectionHandler}
                isSplitApprovalFlow={isSplitApprovalFlow}
              />
            </div>
          ))}
      </div>
      {children}
    </div>
  );
}
